import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar,
  Clock,
  User,
  ArrowRight,
  TrendingUp,
  Brain,
  Lightbulb,
  BookOpen,
  Tag,
  Search,
  ChevronRight,
  BarChart3,
  Star,
  FileText,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  published: boolean;
  read_time: number;
  meta_title?: string;
  meta_description?: string;
  og_image_url?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  view_count: number;
}

interface CategoryData {
  name: string;
  post_count: number;
}

const Blog = () => {
  // Set SEO-optimized title for Blog page
  usePageTitle('blog');
  
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Fetch blog posts from API
  useEffect(() => {
    fetchBlogPosts();
    fetchCategories();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch('/api/blog/posts?published=true&limit=100');
      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data);
      } else {
        toast.error('Failed to load blog posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/blog/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };


  // Filter posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find(post => post.featured);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'Success Stories': return <TrendingUp className="w-20 h-20 text-dineai-yellow/30" />;
      case 'AI Technology': return <Brain className="w-20 h-20 text-white/30" />;
      case 'Tips & Tricks': return <Lightbulb className="w-20 h-20 text-dineai-yellow/30" />;
      case 'Analytics': return <BarChart3 className="w-20 h-20 text-white/30" />;
      case 'Menu Strategy': return <FileText className="w-20 h-20 text-white/30" />;
      case 'Business': return <TrendingUp className="w-20 h-20 text-dineai-yellow/30" />;
      default: return <Brain className="w-20 h-20 text-white/30" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1f4ed1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dineai-yellow"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1f4ed1]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2256e5] to-[#1f4ed1] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-dineai-yellow/20 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-dineai-yellow" />
              <span className="text-white font-medium text-sm">DINE AI BLOG</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Restaurant AI Insights & 
              <span className="block bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                Menu Intelligence Trends
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 mb-8">
              Expert insights on AI in restaurants, menu optimization strategies, and industry innovations.
              Learn how leading restaurants are transforming with AI.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search articles, topics, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg bg-white rounded-xl shadow-xl border-0"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-16 bg-[#1f4ed1]">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl font-bold mb-8 text-center text-white">Featured Article</h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 bg-dineai-yellow text-black rounded-full text-sm font-semibold">
                        FEATURED
                      </span>
                      <span className="text-white/80">{featuredPost.category}</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4 text-white">
                      {featuredPost.title}
                    </h3>
                    
                    <p className="text-white/80 mb-6">
                      {featuredPost.excerpt}
                    </p>
                    
                    <div className="flex items-center gap-6 text-sm text-white/70 mb-6">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{featuredPost.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(featuredPost.published_at || featuredPost.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{featuredPost.read_time} min read</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        <span>{featuredPost.view_count} views</span>
                      </div>
                    </div>
                    
                    <Link to={`/blog/${featuredPost.slug}`}>
                      <Button className="bg-dineai-yellow hover:bg-dineai-yellow/90 text-black font-bold">
                        Read Full Article
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="relative h-80 bg-white/10 rounded-xl overflow-hidden">
                    {featuredPost.og_image_url ? (
                      <img 
                        src={featuredPost.og_image_url} 
                        alt={featuredPost.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Brain className="w-32 h-32 text-white/20" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-dineai-yellow text-black'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
              }`}
            >
              All Articles
            </button>
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.name
                    ? 'bg-dineai-yellow text-black'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                {category.name} ({category.post_count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredPosts.map((post) => (
              <motion.article
                key={post.id}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden hover:bg-white/20 transition-all group"
              >
                {/* Article Image */}
                <div className="h-48 bg-white/5 relative overflow-hidden">
                  {post.og_image_url ? (
                    <img 
                      src={post.og_image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      {getCategoryIcon(post.category)}
                    </div>
                  )}
                  {post.featured && (
                    <div className="absolute top-4 right-4">
                      <Star className="w-6 h-6 text-dineai-yellow fill-dineai-yellow" />
                    </div>
                  )}
                </div>
                
                {/* Article Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm text-dineai-yellow font-semibold">
                      {post.category}
                    </span>
                    <span className="text-white/50">•</span>
                    <span className="text-sm text-white/70">
                      {post.read_time} min read
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-dineai-yellow transition-colors">
                    {post.title}
                  </h3>
                  
                  <p className="text-white/80 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="text-dineai-yellow font-medium flex items-center gap-1 hover:gap-2 transition-all"
                    >
                      Read More
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-white/10 text-white/70 rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-white/70 text-lg">
                No articles found matching your search criteria.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-r from-[#2256e5] to-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Updated with Restaurant AI Trends
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Get weekly insights on AI in restaurants, menu optimization tips, and exclusive success stories delivered to your inbox.
            </p>
            
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 py-6 px-4 text-lg bg-white rounded-lg"
              />
              <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold">
                Subscribe
              </Button>
            </form>
            
            <p className="text-sm text-blue-100 mt-4">
              Join 2,000+ restaurant owners and managers. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;